import { MESApiService, type spParameter } from "@dexteel/mesf-core";
import { get } from "lodash-es";
import type {
	CutRollsPayload,
	OperatorOption,
	PassStatusOption,
	RollsetStatusChange,
	RollsProductionRow,
} from "../models/Rollshop";

const rowsOf = <T>(resp: unknown) =>
	get(resp, "data.tables[0].rows", []) as T[];

export const getRollsProduction = async ({
	rollsetId,
	signal,
}: {
	rollsetId: number;
	signal?: AbortSignal;
}): Promise<RollsProductionRow[]> => {
	const apiService = new MESApiService();
	const resp = await apiService.callV2(
		"[ROLLS].[GetRollsProduction]",
		[{ name: "rollsetId", value: rollsetId }],
		signal,
	);
	if (resp.ok) return rowsOf<RollsProductionRow>(resp);
	throw new Error(resp.message || "Error fetching the pass condition");
};

export const getRollsetStatusValidChanges = async ({
	signal,
}: {
	signal?: AbortSignal;
} = {}): Promise<RollsetStatusChange[]> => {
	const apiService = new MESApiService();
	const resp = await apiService.callV2(
		"[ROLLS].[GetRollsetStatusValidChanges]",
		[],
		signal,
	);
	if (resp.ok) return rowsOf<RollsetStatusChange>(resp);
	throw new Error(
		resp.message || "Error fetching the valid Op. Status changes",
	);
};

export const getActiveOperators = async ({
	signal,
}: {
	signal?: AbortSignal;
} = {}): Promise<OperatorOption[]> => {
	const apiService = new MESApiService();
	const resp = await apiService.callV2(
		"[MILL].[GetOperators]",
		[{ name: "OnlyActives", value: true }],
		signal,
	);
	if (resp.ok) return rowsOf<OperatorOption>(resp);
	throw new Error(resp.message || "Error fetching operators");
};

export const getPassStatus = async ({
	signal,
}: {
	signal?: AbortSignal;
} = {}): Promise<PassStatusOption[]> => {
	const apiService = new MESApiService();
	const resp = await apiService.callV2("[MILL].[GetPassStatus]", [], signal);
	if (resp.ok) return rowsOf<PassStatusOption>(resp);
	throw new Error(resp.message || "Error fetching pass status");
};

const toRollsXml = (rolls: CutRollsPayload["rolls"]) =>
	`<XMLRoot>${rolls
		.map(
			(roll) =>
				`<Row position="${roll.position}" diameter="${roll.newDiameter}"${
					roll.timeToCut == null ? "" : ` timeToCut="${roll.timeToCut}"`
				} />`,
		)
		.join("")}</XMLRoot>`;

export const cutRolls = async (payload: CutRollsPayload): Promise<void> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "rollsetId", value: payload.rollsetId },
		{ name: "rolls", value: toRollsXml(payload.rolls) },
		{ name: "operator1Id", value: payload.operator1Id },
		{ name: "operator2Id", value: payload.operator2Id },
		{ name: "comments", value: payload.comments },
	];
	const resp = await apiService.callV2("[ROLLS].[CutRolls]", parameters);
	if (!resp.ok) throw new Error(resp.message || "Error turning the rolls");
};

export const updateRollsProduction = async ({
	idBottom,
	idTop,
	passStatusCode,
}: {
	idBottom: number | null;
	idTop: number | null;
	passStatusCode: string;
}): Promise<void> => {
	const apiService = new MESApiService();
	const resp = await apiService.callV2("[ROLLS].[UpdateRollsProduction]", [
		{ name: "idBottom", value: idBottom },
		{ name: "idTop", value: idTop },
		{ name: "passStatusCode", value: passStatusCode },
	]);
	if (!resp.ok)
		throw new Error(resp.message || "Error updating the pass status");
};
